import React, { useState, useEffect, useCallback } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { listOutstockDeclarations, updateOutstockDeclaration, deleteOutstockDeclaration, listOutstockCorrections, createOutstockCorrection, updateOutstockCorrection, deleteOutstockCorrection } from '../services/outstockService';
import { OutstockDeclaration, OutstockDeclarationsListParams } from '../types/outstock';
import { Article } from '../types/api.types';
import ArticleSelector from './ArticleSelector';
import { exportOutstockDeclarationsToCSV, downloadCSVWithBOM } from '../utils/csvExport';
import { 
  Edit, 
  Trash2, 
  Search,
  Calendar,
  FileText,
  Package,
  Plus,
  Download
} from 'lucide-react';

interface OutstockDeclarationsTableProps {
  loading?: boolean;
}

const OutstockDeclarationsTable: React.FC<OutstockDeclarationsTableProps> = ({ loading = false }) => {
  const { activeDatabase } = useDatabaseContext();
  const [declarations, setDeclarations] = useState<OutstockDeclaration[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Editing state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    start_date: string;
    end_date: string;
  }>({ start_date: '', end_date: '' });

  // Filters
  const [filters, setFilters] = useState<OutstockDeclarationsListParams>({});
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [monthColumns, setMonthColumns] = useState<string[]>([]);
  const [rowBuffers, setRowBuffers] = useState<Record<string, Record<string, { id?: number; value: number; deleted?: boolean }>>>({});
  const [rowsLoading, setRowsLoading] = useState<Record<string, boolean>>({});
  const [initialBuffers, setInitialBuffers] = useState<Record<string, Record<string, number>>>({});
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});

  const computeMonthsRange = (startISO: string, endISO: string) => {
    const res: string[] = [];
    const start = new Date(startISO);
    const end = new Date(endISO);
    let y = start.getFullYear();
    let m = start.getMonth();
    const yEnd = end.getFullYear();
    const mEnd = end.getMonth();
    while (y < yEnd || (y === yEnd && m <= mEnd)) {
      const mm = String(m + 1).padStart(2, '0');
      res.push(`${y}-${mm}-01`);
      m += 1;
      if (m === 12) { m = 0; y += 1; }
    }
    return res;
  };

  const toggleExpand = async (declaration: OutstockDeclaration) => {
    if (!activeDatabase?.id) return;
    if (expandedId === declaration.id) {
      setExpandedId(null);
      setMonthColumns([]);
      setRowBuffers({});
      return;
    }
    setExpandedId(declaration.id);
    const months = computeMonthsRange(declaration.start_date, declaration.end_date);
    setMonthColumns(months);
    const subIds = declaration.substitutes_articles.map(([id]) => String(id));
    const buffers: Record<string, Record<string, { id?: number; value: number; deleted?: boolean }>> = {};
    const originals: Record<string, Record<string, number>> = {};
    const loading: Record<string, boolean> = {};
    subIds.forEach(id => loading[id] = true);
    setRowsLoading(loading);
    for (const sid of subIds) {
      try {
        const list = await listOutstockCorrections(String(activeDatabase.id), { declaration_id: String(declaration.id), article_id: sid });
        const map: Record<string, { id?: number; value: number; deleted?: boolean }> = {};
        const base: Record<string, number> = {};
        // Initialize with 0 for all months
        months.forEach(m => { map[m] = { value: 0 }; });
        // Fill with persisted quantities
        list.forEach(c => {
          const monthKey = `${c.correction_date.slice(0,7)}-01`;
          if (map[monthKey]) {
            map[monthKey] = { id: c.id, value: c.quantity };
            base[monthKey] = c.quantity;
          }
        });
        buffers[sid] = map;
        originals[sid] = base;
      } finally {
        setRowsLoading(prev => ({ ...prev, [sid]: false }));
      }
    }
    setRowBuffers(buffers);
    setInitialBuffers(originals);
  };

  const changeRowValue = (sid: string, month: string, value: number) => {
    setRowBuffers(prev => ({
      ...prev,
      [sid]: { ...(prev[sid] || {}), [month]: { ...((prev[sid] || {})[month] || {}), value, deleted: false } }
    }));
  };

  const saveRow = async (declaration: OutstockDeclaration, sid: string) => {
    if (!activeDatabase?.id) return;
    const row = rowBuffers[sid] || {};
    setRowsLoading(prev => ({ ...prev, [sid]: true }));
    try {
      for (const month of monthColumns) {
        const entry = row[month];
        if (!entry) continue;
        if (entry.id) {
          await updateOutstockCorrection(String(activeDatabase.id), String(entry.id), { quantity: entry.value });
        } else if (!entry.deleted && entry.value !== 0) {
          await createOutstockCorrection(String(activeDatabase.id), { article_id: sid, correction_date: month, quantity: entry.value, declaration_id: String(declaration.id) });
        }
      }
      // After successful save, reset originals to current values and flash green
      setInitialBuffers(prev => ({
        ...prev,
        [sid]: Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v.value]))
      }));
      setSavedFlash(prev => ({ ...prev, [sid]: true }));
      setTimeout(() => {
        setSavedFlash(prev => ({ ...prev, [sid]: false }));
      }, 1200);
    } finally {
      setRowsLoading(prev => ({ ...prev, [sid]: false }));
    }
  };

  // Fetch declarations
  const fetchDeclarations = useCallback(async () => {
    if (!activeDatabase?.id) return;

    setTableLoading(true);
    setError(null);
    try {
      const data = await listOutstockDeclarations(String(activeDatabase.id), filters);
      setDeclarations(data);
    } catch (err: any) {
      setError('Erreur lors du chargement des déclarations');
      console.error('Error fetching declarations:', err);
    } finally {
      setTableLoading(false);
    }
  }, [activeDatabase?.id, filters]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchDeclarations();
  }, [fetchDeclarations]);

  // Date edit modal
  const [dateEditOpen, setDateEditOpen] = useState(false);
  const handleEditStart = (declaration: OutstockDeclaration) => {
    // Collapse any expanded corrections grid
    setExpandedId(null);
    setMonthColumns([]);
    setRowBuffers({});
    // Open date edit modal
    setEditingId(declaration.id);
    setEditForm({ start_date: declaration.start_date, end_date: declaration.end_date });
    setDateEditOpen(true);
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({ start_date: '', end_date: '' });
    setDateEditOpen(false);
  };

  // Handle edit save
  const handleEditSave = async (declarationId: number) => {
    if (!activeDatabase?.id) return;

    try {
      const updatedDeclaration = await updateOutstockDeclaration(
        String(activeDatabase.id), 
        String(declarationId), 
        editForm
      );
      
      setDeclarations(prev => 
        prev.map(d => d.id === declarationId ? updatedDeclaration : d)
      );
      setEditingId(null);
      setEditForm({ start_date: '', end_date: '' });
      setDateEditOpen(false);
    } catch (err: any) {
      setError('Erreur lors de la mise à jour');
      console.error('Error updating declaration:', err);
    }
  };

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  
  // Delete substitute modal state
  const [deleteSubModalOpen, setDeleteSubModalOpen] = useState(false);
  const [deleteSubContext, setDeleteSubContext] = useState<{
    declarationId: number;
    substituteId: string;
  } | null>(null);

  // Handle delete
  const handleDeleteClick = (declarationId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row expansion
    setDeleteTargetId(declarationId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!activeDatabase?.id || !deleteTargetId) return;

    try {
      await deleteOutstockDeclaration(String(activeDatabase.id), String(deleteTargetId));
      setDeclarations(prev => prev.filter(d => d.id !== deleteTargetId));
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
    } catch (err: any) {
      setError('Erreur lors de la suppression');
      console.error('Error deleting declaration:', err);
    }
  };

  const handleDeleteSubConfirm = async () => {
    if (!activeDatabase?.id || !deleteSubContext) return;
    const { declarationId, substituteId } = deleteSubContext;

    try {
      // 1) Update declaration by removing the substitute
      const declaration = declarations.find(d => d.id === declarationId);
      if (!declaration) return;

      const nextProducts = declaration.substitutes_articles.filter(([id]) => String(id) !== substituteId);
      try {
        await updateOutstockDeclaration(String(activeDatabase.id), String(declarationId), { 
          products: nextProducts as any, 
          start_date: declaration.start_date, 
          end_date: declaration.end_date 
        } as any);
      } catch (err) {
        // If backend does not accept products, continue to delete corrections and update UI locally
      }

      // 2) Delete related corrections
      const list = await listOutstockCorrections(String(activeDatabase.id), { 
        declaration_id: String(declarationId), 
        article_id: substituteId 
      });
      for (const c of list) {
        await deleteOutstockCorrection(String(activeDatabase.id), String(c.id));
      }

      // 3) Update UI state
      setDeclarations(prev => prev.map(d => 
        d.id === declarationId ? { ...d, substitutes_articles: nextProducts } : d
      ));
      setRowBuffers(prev => {
        const copy = { ...prev };
        delete copy[substituteId];
        return copy;
      });

      // 4) Close modal and clear context
      setDeleteSubModalOpen(false);
      setDeleteSubContext(null);
    } catch (err) {
      console.error('Error deleting substitute:', err);
      setError('Erreur lors de la suppression du substitut');
    }
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev: OutstockDeclarationsListParams) => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  // Handle filter clear
  const handleFilterClear = () => {
    setFilters({});
  };

  // Handle export to CSV
  const handleExport = () => {
    const csvContent = exportOutstockDeclarationsToCSV(declarations);
    const today = new Date().toISOString().slice(0, 10);
    const filename = `declarations_rupture_${today}.csv`;
    downloadCSVWithBOM(csvContent, filename);
  };

  // (Corrections modal removed; inline collapsible editor is used)

  // ArticleSelector modal for adding substitutes (triggered from collapsible)
  const [addSubsOpen, setAddSubsOpen] = useState(false);
  const [tempSelectedArticles, setTempSelectedArticles] = useState<Article[]>([]);
  const openAddSubstitutes = () => { setTempSelectedArticles([]); setAddSubsOpen(true); };
  const confirmAddSubstitutes = () => { setAddSubsOpen(false); };

  if (!activeDatabase) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune base de données sélectionnée
          </h3>
          <p className="text-gray-600">
            Veuillez sélectionner une base de données pour voir les déclarations de rupture.
          </p>
      </div>
      {/* CorrectionsModal removed in favor of inline collapsible editor */}
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-600" />
          <div className="font-medium">Déclarations de Rupture ({declarations.length})</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-1.5 rounded border ${showFilters ? 'bg-blue-50 border-blue-300' : 'border-gray-300'} hover:bg-gray-50 flex items-center gap-2 text-sm`}
          >
            <Search size={16} />
            Filtres
          </button>
          <button
            onClick={handleExport}
            disabled={tableLoading || declarations.length === 0}
            className="px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2 text-sm"
            title="Exporter en CSV"
          >
            <Download size={16} />
            Exporter
          </button>
          <button
            onClick={fetchDeclarations}
            disabled={tableLoading}
            className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-3 bg-gray-50 border rounded mb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Article ID</label>
              <input
                type="text"
                value={filters.article_id || ''}
                onChange={(e) => handleFilterChange('article_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="ID article"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début rupture</label>
              <input
                type="date"
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin rupture</label>
              <input
                type="date"
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleFilterClear}
                className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                Effacer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-3 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto border rounded">
        {tableLoading || loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Package size={24} className="animate-pulse mx-auto text-blue-600 mb-2" />
              <p className="text-gray-600">Chargement...</p>
            </div>
          </div>
        ) : declarations.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-4">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Aucune déclaration de rupture trouvée</p>
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Article</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période rupture</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Substituts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Déclaré le</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {declarations.map((declaration) => (
                <React.Fragment key={declaration.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(declaration)}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{declaration.article_id}</div>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleEditStart(declaration); }}
                      className="group text-left"
                      title="Modifier les dates"
                    >
                      <div className="text-sm text-gray-900 inline-flex items-center gap-2">
                        <div>
                          <div>Du {new Date(declaration.start_date).toLocaleDateString('fr-FR')}</div>
                          <div>Au {new Date(declaration.end_date).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <Edit size={16} className="text-gray-400 group-hover:text-blue-600" />
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    {editingId === declaration.id ? (
                      <div className="w-80 text-sm text-gray-600">Les substituts se gèrent dans la section détaillée ci-dessous.</div>
                    ) : (
                      <div className="text-sm text-gray-700">
                        {declaration.substitutes_articles.length} substituts
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(declaration.declaration_date).toLocaleDateString('fr-FR')}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleDeleteClick(declaration.id, e)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === declaration.id && (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 bg-gray-50">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr>
                              <th className="text-left px-2 py-2">Substitut</th>
                              {monthColumns.map(m => (
                                <th key={m} className="text-right px-2 py-2 whitespace-nowrap">{new Date(m).toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' })}</th>
                              ))}
                              <th className="text-right px-2 py-2">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {declaration.substitutes_articles.map(([sid]) => {
                              const sidStr = String(sid);
                              const row = rowBuffers[sidStr] || {};
                              return (
                                <tr key={sidStr} className="border-t">
                                  <td className="px-2 py-2 font-medium flex items-center gap-2">
                                    <span>{sidStr}</span>
                                    <button
                                      className="text-red-600 hover:text-red-800"
                                      title="Supprimer le substitut"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!activeDatabase?.id) return;
                                        setDeleteSubContext({
                                          declarationId: declaration.id,
                                          substituteId: sidStr
                                        });
                                        setDeleteSubModalOpen(true);
                                      }}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                  {monthColumns.map(m => {
                                    const cell = row[m] || { value: 0 };
                                    const original = (initialBuffers[sidStr] || {})[m] ?? 0;
                                    const modified = cell.value !== original;
                                    const bg = savedFlash[sidStr]
                                      ? 'bg-green-50'
                                      : (modified ? 'bg-yellow-50' : '');
                                    return (
                                      <td key={sidStr + m} className={`px-2 py-1 text-right ${bg}`}>
                                        <div className="flex items-center justify-end gap-1">
                                          <input type="number" className="w-24 text-right border rounded px-2 py-1" value={cell.value} onChange={(e) => changeRowValue(sidStr, m, parseFloat(e.target.value) || 0)} step={1} />
                                        </div>
                                      </td>
                                    );
                                  })}
                                  <td className="px-2 py-2 text-right whitespace-nowrap">
                                    <button className="px-2 py-1 border rounded text-sm" disabled={rowsLoading[sidStr]} onClick={() => saveRow(declaration, sidStr)}>
                                      {rowsLoading[sidStr] ? '...' : 'Enregistrer'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr>
                              <td className="px-2 py-2" colSpan={monthColumns.length + 2}>
                                <button onClick={openAddSubstitutes} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                  <Plus size={16} /> Ajouter un substitut
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* CorrectionsModal removed in favor of inline collapsible editor */}
      <AddSubstitutesModal
        open={addSubsOpen}
        onClose={() => setAddSubsOpen(false)}
        onConfirm={confirmAddSubstitutes}
        selected={tempSelectedArticles}
        setSelected={setTempSelectedArticles}
      />
      {dateEditOpen && editingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={handleEditCancel}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div className="font-semibold">Modifier les dates</div>
              <button onClick={handleEditCancel} className="text-gray-600">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Date de début</label>
                <input type="date" value={editForm.start_date} onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Date de fin</label>
                <input type="date" value={editForm.end_date} onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value }))} className="w-full px-3 py-2 border rounded" />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
              <button onClick={handleEditCancel} className="px-3 py-2 border rounded">Annuler</button>
              <button onClick={() => handleEditSave(editingId)} className="px-3 py-2 bg-blue-600 text-white rounded">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete declaration modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setDeleteModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 flex flex-col items-center">
              <div className="mb-3">
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Êtes-vous sûr de vouloir supprimer cette déclaration ? Cette action est irréversible.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete substitute modal */}
      {deleteSubModalOpen && deleteSubContext && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setDeleteSubModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 flex flex-col items-center">
              <div className="mb-3">
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirmer la suppression du substitut
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Êtes-vous sûr de vouloir supprimer ce substitut et toutes ses corrections ? Cette action est irréversible.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDeleteSubModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteSubConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstockDeclarationsTable;

// Corrections Modal
// Basic inline modal for editing monthCorrections buffer and saving
export const CorrectionsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  loading: boolean;
  declaration: OutstockDeclaration | null;
  substituteId: string;
  monthCorrections: Record<string, number>;
  onChange: (date: string, value: number) => void;
  onSave: () => void;
}> = ({ open, onClose, loading, declaration, substituteId, monthCorrections, onChange, onSave }) => {
  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open || !declaration) return null;
  const dates = Object.keys(monthCorrections).sort();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">Corrections — Déclaration #{declaration.id} — Substitut {substituteId}</div>
          <button onClick={onClose} className="text-gray-600">✕</button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-auto">
          {loading ? (
            <div className="text-center text-gray-600">Chargement…</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1">Mois</th>
                  <th className="text-right px-2 py-1">Impact</th>
                </tr>
              </thead>
              <tbody>
                {dates.map((d) => (
                  <tr key={d} className="border-t">
                    <td className="px-2 py-1">{new Date(d).toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' })}</td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        className="w-28 text-right border rounded px-2 py-1"
                        value={monthCorrections[d] ?? 0}
                        onChange={(e) => onChange(d, parseFloat(e.target.value) || 0)}
                        step="1"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 border rounded">Annuler</button>
          <button onClick={onSave} className="px-3 py-2 bg-blue-600 text-white rounded" disabled={loading}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

// Add substitutes modal using ArticleSelector
export const AddSubstitutesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selected: Article[];
  setSelected: (arts: Article[]) => void;
}> = ({ open, onClose, onConfirm, selected, setSelected }) => {
  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">Ajouter des substituts</div>
          <button onClick={onClose} className="text-gray-600">✕</button>
        </div>
        <div className="p-5">
          <ArticleSelector 
            onSelectionChange={() => {}}
            onApplyFilters={() => {}}
            onArticlesChange={(arts) => setSelected(arts)}
          />
          <div className="mt-3 text-sm text-gray-600">Sélectionnés: {selected.length}</div>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 border rounded">Annuler</button>
          <button onClick={onConfirm} className="px-3 py-2 bg-blue-600 text-white rounded">Ajouter</button>
        </div>
      </div>
    </div>
  );
};
